import { usePortfolioGroupParams } from "@app/hooks/usePortfolioGroupParams";
import { usePortfolioGroupInfo } from "../../usePortfolioGroupInfo";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { CarPageContent } from "@app/components/PageContent";
import {
  showConfigToDataIds,
  usePortfolioGroupVersionData,
  UsePortfolioGroupVersionDataParams,
} from "../../usePortfolioGroupVersionData";
import { AnalyzeModuleStyledPage } from "./AnalyzeModuleStyledPage";
import { useShowConfigSelector } from "../useShowConfigSelector";
import { ShowConfigSelector } from "../ShowConfigSelector";
import { ChartHistoricalMarketSummary } from "../ChartHistoricalMarketsSummary";
import { Typography } from "@mui/material";
import { PortfolioGroupAssetStatTable } from "@app/pages/assetAllocation/PortfolioGroupAssetStatTable";
import { usePortfolioGroupVersionStatTableData } from "@app/pages/assetAllocation/usePortfolioGroupVersionStatTableData";
import {
  assetAllocationPages,
  portfolioAnalysisPages,
} from "@app/const/route.const";
import { CarDownloadButton } from "@app/components/Buttons";
import { openInNewTab } from "@app/utils";

interface AnalyzeModuleScenarioAnalysisProps {
  versionParams: UsePortfolioGroupVersionDataParams;
  moduleId: string;
}

export const AnalyzeModuleScenarioAnalysis = (
  props: AnalyzeModuleScenarioAnalysisProps,
) => {
  const { portfolioGroupId } = usePortfolioGroupParams();

  const pageContent = usePageContent();

  const { moduleId } = props;

  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });

  const showConfigSelector = useShowConfigSelector({
    portfolioGroupId,
    portfolioGroupVersionId: props.versionParams.versionId,
    publishedVersionId: props.versionParams.publishedVersionId,
  });

  const data = usePortfolioGroupVersionData({
    ...props.versionParams,
    show: showConfigSelector.config,
  });

  const versionStatTableData = usePortfolioGroupVersionStatTableData({
    bands: data.tableData.bands,
    skip: data.assetAllocationIsLoading,
    simulationSuffix: groupInfo.getSimulationSuffix(true),
  });

  const handleShowPdf = () => {
    if (showConfigSelector.config) {
      const params = {
        portfolioGroupId,
        moduleId: props.moduleId,
        dataIds: showConfigToDataIds(showConfigSelector.config),
      };

      openInNewTab(
        groupInfo.isSinglePortfolioMode
          ? portfolioAnalysisPages.getPdfScenarioAnalysisReportUrl(params)
          : assetAllocationPages.getPdfModuleScenarioAnalysisReportUrl(params),
      );
    }
  };

  return (
    <AnalyzeModuleStyledPage>
      <TablePlaceHolder
        isLoading={
          groupInfo.isLoading ||
          data.isLoading ||
          versionStatTableData.isLoading ||
          pageContent.isLoading
        }
        rows={5}
      >
        <CarPageContent
          isWhiteContext
          content={pageContent.getContent(
            groupInfo.isSinglePortfolioMode
              ? CarPageContentCode.PORTFOLIO_ANALYSIS_ANALYZE_SCENARIO_ANALYSIS
              : CarPageContentCode.ANALYZE_PORTFOLIO_GROUP_REPORTS_MODULE_SCENARIO_ANALYSIS,
          )}
        />
        <CarDownloadButton
          onClick={handleShowPdf}
          sx={{ mt: 2, mb: -2, zIndex: 1, ml: "auto", alignSelf: "flex-end" }}
        >
          Download PDF
        </CarDownloadButton>
        <ShowConfigSelector showConfigSelector={showConfigSelector} />
        <Typography variant="h6SemiBold" sx={{ mt: 5, ml: 10 }}>
          Historical Markets Summary
        </Typography>
        <ChartHistoricalMarketSummary
          sx={{ mt: 1 }}
          data={versionStatTableData.statTableData}
          moduleId={moduleId}
        />
        <PortfolioGroupAssetStatTable
          sx={{ mt: 5 }}
          tableData={data.tableData}
          rows={versionStatTableData.statTableData.historicalStatRows}
          showPercentile={false}
        />
      </TablePlaceHolder>
    </AnalyzeModuleStyledPage>
  );
};
